/**
 * OperatorSupplierDrawer — drawer único para Operador Logístico OU Fornecedor.
 *
 * Modes:
 *   - kind='operator' : 1 ativo por physical_product.
 *   - kind='supplier' : até 3 por physical_product, identificados por
 *     supplier_position (1..3).
 *
 * Fluxos:
 *   - mode='invite' : convidar por email + lookup automático.
 *   - mode='edit'   : ajustar fixed_amount_cents do vínculo existente.
 *
 * Spec: docs/V2/FEATURES/OPERADOR_E_FORNECEDORES.md
 */

import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { logisticsRepsV2API } from '../../../services/api/v2/logisticsReps';
import { suppliersV2API } from '../../../services/api/v2/suppliers';
import { usersV2API } from '../../../services/api/v2/users';
import { parseApiError, scrollToFirstError } from '../../../utils/apiErrors';
import Drawer from './Drawer';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function brlToCents(value) {
  const cleaned = String(value).replace(/[^\d,.]/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  if (Number.isNaN(num) || num < 0) return null;
  return Math.round(num * 100);
}

function centsToBrl(cents) {
  if (cents == null) return '';
  return (Number(cents) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

const OperatorSupplierDrawer = ({
  isOpen,
  onClose,
  kind,                 // 'operator' | 'supplier'
  mode = 'invite',      // 'invite' | 'edit'
  physicalProduct,      // { id, name }
  supplierPosition,     // 1 | 2 | 3 (só pra supplier)
  current,              // vínculo existente (mode='edit')
  onSaved,
}) => {
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');
  const [lookup, setLookup] = useState({ status: 'idle', exists: null, name: null });
  const lookupTimer = useRef(null);

  const isOperator = kind === 'operator';
  const isEditing = mode === 'edit';

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setTopError('');
    if (isEditing && current) {
      setEmail(current.rep_email || current.supplier_email || '');
      setAmount(centsToBrl(current.fixed_amount_cents));
    } else {
      setEmail('');
      setAmount('');
    }
    setLookup({ status: 'idle', exists: null, name: null });
  }, [isOpen, mode, current, isEditing]);

  useEffect(() => {
    if (isEditing) return; // sem lookup no edit
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    const clean = email.trim().toLowerCase();
    if (!clean || !EMAIL_REGEX.test(clean)) {
      setLookup({ status: 'idle', exists: null, name: null });
      return;
    }
    setLookup({ status: 'loading', exists: null, name: null });
    lookupTimer.current = setTimeout(async () => {
      try {
        const res = await usersV2API.lookupByEmail(clean);
        const data = res.data || {};
        setLookup({ status: 'done', exists: !!data.exists, name: data.name || null });
      } catch {
        setLookup({ status: 'error', exists: null, name: null });
      }
    }, 500);
    return () => clearTimeout(lookupTimer.current);
  }, [email, isEditing]);

  const validate = () => {
    const err = {};
    if (!isEditing) {
      const clean = email.trim();
      if (!clean) err.email = 'Email é obrigatório';
      else if (!EMAIL_REGEX.test(clean)) err.email = 'Email inválido';
    }
    if (amount === '' || amount == null) {
      err.amount = 'Valor é obrigatório';
    } else {
      const cents = brlToCents(amount);
      if (cents == null) err.amount = 'Use um número >= 0';
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      const cents = brlToCents(amount);

      let res;
      if (isEditing) {
        if (isOperator) {
          res = await logisticsRepsV2API.updateFixedAmount(physicalProduct.id, {
            fixed_amount_cents: cents,
          });
        } else {
          res = await suppliersV2API.updateFixedAmount(physicalProduct.id, supplierPosition, {
            fixed_amount_cents: cents,
          });
        }
        toast.success('Valor atualizado');
      } else {
        const payload = {
          physical_product_id: physicalProduct.id,
          email: email.trim().toLowerCase(),
          fixed_amount_cents: cents,
        };
        if (isOperator) {
          res = await logisticsRepsV2API.invite(payload);
        } else {
          payload.supplier_position = supplierPosition;
          res = await suppliersV2API.invite(payload);
        }
        const data = res.data || {};
        if (data.mode === 'linked') {
          toast.success(`${isOperator ? 'Operador' : 'Fornecedor'} vinculado com sucesso`);
        } else {
          toast.success(`Convite enviado para ${payload.email}`);
        }
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Erro ao salvar');
      if (Object.keys(fieldErrors).length) {
        setErrors((p) => ({ ...p, ...fieldErrors }));
        scrollToFirstError(fieldErrors);
      }
      setTopError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEditing
    ? (isOperator ? 'Editar Operador Logístico' : `Editar Fornecedor (slot ${supplierPosition})`)
    : (isOperator ? 'Convidar Operador Logístico' : `Convidar Fornecedor (slot ${supplierPosition})`);

  const subtitle = isOperator
    ? '1 operador ativo por item do catálogo'
    : 'Até 3 fornecedores ativos por item (slots 1, 2, 3)';

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon={isOperator ? 'mdi:truck-delivery-outline' : 'mdi:factory'}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={isEditing ? 'Salvar' : 'Enviar convite'}
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      <div className="drawer-field">
        <span className="drawer-label">Item do catálogo</span>
        <div className="drawer-input" style={{ background: '#f9fafb', cursor: 'default' }}>
          {physicalProduct?.name || `Produto físico #${physicalProduct?.id}`}
        </div>
      </div>

      {!isEditing && (
        <div className="drawer-field">
          <span className="drawer-label drawer-label--required">
            Email do {isOperator ? 'operador' : 'fornecedor'}
          </span>
          <input
            className="drawer-input"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((p) => ({ ...p, email: undefined }));
            }}
            placeholder={isOperator ? 'operador@email.com' : 'fornecedor@email.com'}
            data-field="email"
            autoComplete="off"
          />
          <LookupHint lookup={lookup} email={email} />
          {errors.email && (
            <span className="drawer-error">
              <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.email}
            </span>
          )}
        </div>
      )}

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Valor fixo por venda (R$)</span>
        <div className="drawer-input--with-suffix">
          <span className="drawer-input-suffix" style={{ left: 12, right: 'auto' }}>R$</span>
          <input
            className="drawer-input"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setErrors((p) => ({ ...p, amount: undefined }));
            }}
            placeholder="0,00"
            style={{ paddingLeft: 36 }}
            data-field="amount"
          />
        </div>
        <span className="drawer-help">
          Valor descontado em toda venda que inclua este item. Aplicado antes das comissões %.
        </span>
        {errors.amount && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.amount}
          </span>
        )}
      </div>
    </Drawer>
  );
};

const LookupHint = ({ lookup, email }) => {
  const isValid = email.trim() && EMAIL_REGEX.test(email.trim());
  if (!isValid) return null;
  if (lookup.status === 'loading') {
    return (
      <span className="drawer-help" style={{ color: '#6b7280' }}>
        <Icon icon="mdi:loading" className="spin" width="12" /> Verificando…
      </span>
    );
  }
  if (lookup.status === 'done' && lookup.exists) {
    return (
      <span className="drawer-help" style={{ color: '#047857', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Icon icon="mdi:check-circle" width="12" />
        Usuário encontrado{lookup.name ? `: ${lookup.name}` : ''} — será vinculado direto
      </span>
    );
  }
  if (lookup.status === 'done' && !lookup.exists) {
    return (
      <span className="drawer-help" style={{ color: '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Icon icon="mdi:email-outline" width="12" />
        Será enviado convite por email (expira em 7 dias)
      </span>
    );
  }
  return null;
};

export default OperatorSupplierDrawer;
